"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useWaterLeakService = exports.useUpdateServiceRequest = exports.useCreateServiceRequest = exports.useServiceRequestsByAlert = exports.useServiceRequestsBySensor = exports.useServiceRequestsByHub = exports.useServiceRequestsByCustomer = exports.useServiceRequest = exports.useServiceRequests = void 0;
const react_1 = require("react");
const mutations_1 = require("../graphql/mutations");
const queries_1 = require("../graphql/queries");
const amplifyBootstrap_1 = require("../utils/amplifyBootstrap");
const useServiceRequests = (_filter) => {
    const [serviceRequests, setServiceRequests] = (0, react_1.useState)([]);
    const [statusCounts, setStatusCounts] = (0, react_1.useState)({});
    const [loading, setLoading] = (0, react_1.useState)(true);
    const [error, setError] = (0, react_1.useState)(null);
    const fetchServiceRequests = async (limit = 50, nextToken, filter) => {
        setLoading(true);
        setError(null);
        try {
            const { customerId, serviceType, status } = filter || {};
            const [listResponse, countsResponse] = await Promise.all([
                amplifyBootstrap_1.API.graphql({
                    query: queries_1.listServiceRequests,
                    variables: { limit, nextToken, customerId, serviceType, status },
                }),
                amplifyBootstrap_1.API.graphql({
                    query: queries_1.getServiceRequestStatusCounts,
                    variables: { customerId, serviceType },
                }),
            ]);
            const responseData = listResponse.data?.listServiceRequests;
            const serviceRequests = Array.isArray(responseData)
                ? responseData
                : responseData?.items || [];
            const statusCounts = countsResponse.data?.getServiceRequestStatusCounts;
            setServiceRequests(serviceRequests);
            setStatusCounts(statusCounts);
            const paginationNextToken = Array.isArray(responseData) ? undefined : responseData?.nextToken;
            return {
                serviceRequests,
                statusCounts,
                nextToken: paginationNextToken,
                hasMore: !!paginationNextToken,
            };
        }
        catch (error) {
            setError(error);
            console.error('Error fetching service requests:', error);
            setServiceRequests([]);
            return {
                serviceRequests: [],
                statusCounts: {},
                nextToken: undefined,
                hasMore: false,
            };
        }
        finally {
            setLoading(false);
        }
    };
    return {
        serviceRequests,
        statusCounts,
        isLoading: loading,
        error,
        fetchServiceRequests,
    };
};
exports.useServiceRequests = useServiceRequests;
const useServiceRequest = (id) => {
    const [serviceRequest, setServiceRequest] = (0, react_1.useState)(null);
    const [loading, setLoading] = (0, react_1.useState)(true);
    const [error, setError] = (0, react_1.useState)(null);
    (0, react_1.useEffect)(() => {
        if (id) {
            fetchServiceRequest();
        }
    }, [id]);
    const fetchServiceRequest = async () => {
        try {
            setLoading(true);
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getServiceRequest,
                variables: { id },
                authMode: 'userPool',
            });
            if (response && response.data && response.data.getServiceRequest) {
                setServiceRequest(response.data.getServiceRequest);
            }
            else {
                console.warn('Unexpected API response structure:', response);
                setServiceRequest(null);
            }
            setError(null);
        }
        catch (err) {
            setError(err);
            console.error('Error fetching service request:', err);
        }
        finally {
            setLoading(false);
        }
    };
    const refetch = async () => {
        fetchServiceRequest();
    };
    return { serviceRequest, loading, error, refetch };
};
exports.useServiceRequest = useServiceRequest;
const useServiceRequestsByCustomer = (customerId) => {
    const [serviceRequests, setServiceRequests] = (0, react_1.useState)([]);
    const [loading, setLoading] = (0, react_1.useState)(true);
    const [error, setError] = (0, react_1.useState)(null);
    (0, react_1.useEffect)(() => {
        if (customerId) {
            fetchServiceRequests();
        }
    }, [customerId]);
    const fetchServiceRequests = async () => {
        try {
            setLoading(true);
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getServiceRequestsByCustomer,
                variables: { customerId, limit: 100 },
                authMode: 'userPool',
            });
            if (response && response.data) {
                let items = [];
                if (Array.isArray(response.data.getServiceRequestsByCustomer)) {
                    items = response.data.getServiceRequestsByCustomer;
                }
                else if (response.data.getServiceRequestsByCustomer &&
                    Array.isArray(response.data.getServiceRequestsByCustomer.items)) {
                    items = response.data.getServiceRequestsByCustomer.items;
                }
                else if (response.data.getServiceRequestsByCustomer) {
                    items = [response.data.getServiceRequestsByCustomer];
                }
                setServiceRequests(items);
            }
            else {
                console.warn('Unexpected API response structure:', response);
                setServiceRequests([]);
            }
            setError(null);
        }
        catch (err) {
            setError(err);
            console.error('Error fetching service requests by customer:', err);
        }
        finally {
            setLoading(false);
        }
    };
    return { serviceRequests, loading, error };
};
exports.useServiceRequestsByCustomer = useServiceRequestsByCustomer;
const useServiceRequestsByHub = (hubId) => {
    const [serviceRequests, setServiceRequests] = (0, react_1.useState)([]);
    const [loading, setLoading] = (0, react_1.useState)(true);
    const [error, setError] = (0, react_1.useState)(null);
    (0, react_1.useEffect)(() => {
        if (hubId) {
            fetchServiceRequests();
        }
    }, [hubId]);
    const fetchServiceRequests = async () => {
        try {
            setLoading(true);
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getServiceRequestsByHub,
                variables: { hubId, limit: 100 },
                authMode: 'userPool',
            });
            if (response && response.data) {
                let items = [];
                if (Array.isArray(response.data.getServiceRequestsByHub)) {
                    items = response.data.getServiceRequestsByHub;
                }
                else if (response.data.getServiceRequestsByHub &&
                    Array.isArray(response.data.getServiceRequestsByHub.items)) {
                    items = response.data.getServiceRequestsByHub.items;
                }
                else if (response.data.getServiceRequestsByHub) {
                    items = [response.data.getServiceRequestsByHub];
                }
                setServiceRequests(items);
            }
            else {
                console.warn('Unexpected API response structure:', response);
                setServiceRequests([]);
            }
            setError(null);
        }
        catch (err) {
            setError(err);
            console.error('Error fetching service requests by hub:', err);
        }
        finally {
            setLoading(false);
        }
    };
    return { serviceRequests, loading, error };
};
exports.useServiceRequestsByHub = useServiceRequestsByHub;
const useServiceRequestsBySensor = (sensorId) => {
    const [serviceRequests, setServiceRequests] = (0, react_1.useState)([]);
    const [loading, setLoading] = (0, react_1.useState)(true);
    const [error, setError] = (0, react_1.useState)(null);
    (0, react_1.useEffect)(() => {
        if (sensorId) {
            fetchServiceRequests();
        }
    }, [sensorId]);
    const fetchServiceRequests = async () => {
        try {
            setLoading(true);
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getServiceRequestsBySensor,
                variables: { sensorId, limit: 100 },
                authMode: 'userPool',
            });
            if (response && response.data) {
                let items = [];
                if (Array.isArray(response.data.getServiceRequestsBySensor)) {
                    items = response.data.getServiceRequestsBySensor;
                }
                else if (response.data.getServiceRequestsBySensor &&
                    Array.isArray(response.data.getServiceRequestsBySensor.items)) {
                    items = response.data.getServiceRequestsBySensor.items;
                }
                else if (response.data.getServiceRequestsBySensor) {
                    items = [response.data.getServiceRequestsBySensor];
                }
                setServiceRequests(items);
            }
            else {
                console.warn('Unexpected API response structure:', response);
                setServiceRequests([]);
            }
            setError(null);
        }
        catch (err) {
            setError(err);
            console.error('Error fetching service requests by sensor:', err);
        }
        finally {
            setLoading(false);
        }
    };
    return { serviceRequests, loading, error };
};
exports.useServiceRequestsBySensor = useServiceRequestsBySensor;
const useServiceRequestsByAlert = (alertId) => {
    const [serviceRequests, setServiceRequests] = (0, react_1.useState)([]);
    const [loading, setLoading] = (0, react_1.useState)(true);
    const [error, setError] = (0, react_1.useState)(null);
    (0, react_1.useEffect)(() => {
        if (alertId) {
            fetchServiceRequests();
        }
    }, [alertId]);
    const fetchServiceRequests = async () => {
        try {
            setLoading(true);
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getServiceRequestsByAlert,
                variables: { alertId, limit: 100 },
                authMode: 'userPool',
            });
            if (response && response.data) {
                let items = [];
                if (Array.isArray(response.data.getServiceRequestsByAlert)) {
                    items = response.data.getServiceRequestsByAlert;
                }
                else if (response.data.getServiceRequestsByAlert &&
                    Array.isArray(response.data.getServiceRequestsByAlert.items)) {
                    items = response.data.getServiceRequestsByAlert.items;
                }
                else if (response.data.getServiceRequestsByAlert) {
                    items = [response.data.getServiceRequestsByAlert];
                }
                setServiceRequests(items);
            }
            else {
                console.warn('Unexpected API response structure:', response);
                setServiceRequests([]);
            }
            setError(null);
        }
        catch (err) {
            setError(err);
            console.error('Error fetching service requests by alert:', err);
        }
        finally {
            setLoading(false);
        }
    };
    return { serviceRequests, loading, error };
};
exports.useServiceRequestsByAlert = useServiceRequestsByAlert;
const useCreateServiceRequest = () => {
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const create = async (input) => {
        try {
            setLoading(true);
            setError(null);
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.createServiceRequest,
                variables: { input },
                authMode: 'userPool',
            });
            if (response && response.data && response.data.createServiceRequest) {
                return response.data.createServiceRequest;
            }
            else {
                console.warn('Unexpected API response structure:', response);
                return null;
            }
        }
        catch (err) {
            setError(err);
            console.error('Error creating service request:', err);
            return null;
        }
        finally {
            setLoading(false);
        }
    };
    return { create, loading, error };
};
exports.useCreateServiceRequest = useCreateServiceRequest;
const useUpdateServiceRequest = () => {
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const update = async (input) => {
        try {
            setLoading(true);
            setError(null);
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.updateServiceRequest,
                variables: { input },
                authMode: 'userPool',
            });
            if (response && response.data && response.data.updateServiceRequest) {
                return response.data.updateServiceRequest;
            }
            else {
                console.warn('Unexpected API response structure:', response);
                return null;
            }
        }
        catch (err) {
            setError(err);
            console.error('Error updating service request:', err);
            return null;
        }
        finally {
            setLoading(false);
        }
    };
    return { update, loading, error };
};
exports.useUpdateServiceRequest = useUpdateServiceRequest;
const useWaterLeakService = () => {
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const requestService = async (input) => {
        try {
            setLoading(true);
            setError(null);
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.requestWaterLeakService,
                variables: { input },
                authMode: 'userPool',
            });
            if (response && response.data && response.data.requestWaterLeakService) {
                return response.data.requestWaterLeakService;
            }
            else {
                console.warn('Unexpected API response structure:', response);
                return null;
            }
        }
        catch (err) {
            setError(err);
            console.error('Error requesting water leak service:', err);
            return null;
        }
        finally {
            setLoading(false);
        }
    };
    return { requestService, loading, error };
};
exports.useWaterLeakService = useWaterLeakService;
